import { Box, Card, Skeleton, useTheme } from "@mui/material"
import {
  type IconUrl,
  type OpeazOpeType,
  SummaryLinkCard,
} from "@pharmupp/p3-front-commons"
import { Suspense } from "react"
import { ChevronDivider } from "../commons"
import { useMyBriOperationsSummary } from "./useMyBriOperationsSummary"

type ExternalLinkCardProps = {
  title: string
  Icon: IconUrl
  type: OpeazOpeType
  variant?: ExternalLinkCardVariant
  divider?: boolean
}

type ExternalLinkCardVariant = "neutral" | "info" | "warning" | "success"

export const ExternalLinkCard = ({
  title,
  Icon,
  variant = "neutral",
  divider = true,
  type,
}: ExternalLinkCardProps) => (
  <Suspense
    fallback={
      <Skeleton component={Card} variant="rectangular" height="170px" width="100%" />
    }
  >
    <ExternalLinkCardInner
      title={title}
      Icon={Icon}
      variant={variant}
      divider={divider}
      type={type}
    />
  </Suspense>
)

const ExternalLinkCardInner = ({
  title,
  Icon,
  variant = "neutral",
  divider = true,
  type,
}: ExternalLinkCardProps) => {
  const style = useLinkCardStyle(variant)
  const { operations } = useMyBriOperationsSummary()

  return (
    <Box position="relative">
      <SummaryLinkCard
        url={operations[type].url}
        Icon={Icon}
        title={operations[type].count ?? "-"}
        subTitle={title}
        configStyle={style}
      />
      {/* DIVIDER */}
      {!!divider && <ChevronDivider accent />}
    </Box>
  )
}

const useLinkCardStyle = (variant: ExternalLinkCardVariant) => {
  const { palette, typography } = useTheme()
  const commonStyle = (color: string) => ({
    icon: { style: { width: "44px", height: "37px" }, color },
    subTitleStyle: {
      lineHeight: typography.pxToRem(20),
      fontSize: typography.pxToRem(16),
    },
  })

  switch (variant) {
    case "info":
      return {
        borderColor: "#176ae5",
        linkIconColor: "#7d9fd2",
        ...commonStyle("#176ae5"),
      }
    case "warning":
      return {
        borderColor: "#f67848",
        linkIconColor: "#e1997e",
        ...commonStyle("#f67848"),
      }
    case "success":
      return {
        borderColor: palette.success.main,
        linkIconColor: palette.success.light,
        ...commonStyle(palette.success.main),
      }
    default:
      return {
        borderColor: palette.common.darkBlue,
        linkIconColor: palette.common.darkBlue,
        ...commonStyle(palette.common.darkBlue),
      }
  }
}
